import { memoAll } from '../../../CodesForFoundation';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { component, ComponentPolicy, ComponentTuple, field, property } from '../../../concepts/objects';
import { memoUsing } from '../../../concepts/objects/MemoUsing';
import { Host } from '../../../concepts/objects/ObjectSymbols';
import { Point } from '../../../platform/geom/Point';
import { Polyline } from '../../../platform/geom/Polyline';
import { Rect } from '../../../platform/geom/Rect';
import { Colors } from '../../../ui';
import { AdapterInfo, CreateAdapterInfo } from '../../canvas/AdapterInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { AutoBringToFront } from '../../canvas/entities/AutoBringToFront';
import { HasCousins } from '../../canvas/entities/HasCousins';
import { Physical, PotentialInteraction, RelativeTo, TransformHints } from '../../canvas/entities/Physical';
import { RectangularPhysical } from '../../canvas/entities/RectangularPhysical';
import { PreferredTextColor } from '../../canvas/entities/TextConventions';
import { FieldLayout } from '../../canvas/fields/AdaptableField';
import { TextField } from '../../canvas/fields/TextField';
import { TextRuler } from '../../canvas/visualization/TextRuler';
import { commentsOpen } from '../annotations/CommentCommands';
import { TagLayout, TagLike } from '../annotations/TagLayout';
import { TagSupport } from '../annotations/TagSupport';
import { Votable } from '../voting/Votable';
import { Vote, VoteCountAttr } from '../voting/Vote';
import { votesOpen } from '../voting/VotingCommands';
import { TopicLabel } from './TopicFields';
import { TopicIndex } from './TopicIndex';
import { topicPropertyLayout } from './TopicPropertyLayouts';

export const TopicLayout = (object: Topic) => {
    const { width, height } = object;

    const base = new Rect(0, 0, width, height);
    const badges = [] as TagLike[];

    // if (CommentCountAttr.get(object) > 0 || CommentOpenAttr.get(object)) {
    //     badges.push({
    //         id: 'comments',
    //         name: 'comments',
    //         value: (CommentCountAttr.get(object) || 0).toString(),
    //         symbol: 'deqo://icons/hero-mini/chat-bubble-left',
    //     });
    // }

    if (VoteCountAttr.get(object) > 0) {
        badges.push({
            id: 'votes',
            text: `+${VoteCountAttr.get(object)}`,
        });
    }

    // const o = this.owner.cast(Topic);
    //     const hasVotes = o.children.find(x => x.is(Vote));
    //     return this.computeLayout(o.width - (hasVotes ? 25 : 0), o.height);

    const tags = TagLayout([...badges], base);

    const labelWidth = !!badges.length ? Rect.fromMany(Object.values(tags)).left + 5 : width;
    const label = new FieldLayout(Rect.fromEdges(0, 0, labelWidth, height), 2);

    return Object.freeze({ width, height, label, tags });
};

export type TopicLayout = ReturnType<typeof TopicLayout>;

const invocation = defineCommand(
    'topicInvoke',
    null,
    (r, [id, hint]: [string, string?]) => {
        const dispatch = r.call(CommandDispatcher);
        // const objects = r.call(ObjectManager);

        if (hint === 'comments') {
            dispatch(commentsOpen, [id]);
        }
        else if (hint === 'votes') {
            dispatch(votesOpen, [id]);
        }
        // else if (hint === 'flip' && IsConcealedHintVisible.get(objects.get(id, Sticky))) {
        //     dispatch(flip, [[id]]);
        // }
        // else {
        //     const tag = objects.getMaybe(hint, Tag);
        //     if (!!tag) dispatch(tagsToggleEdit, [tag.id, true]);
        // }
    },
    [],
);

const mutation = defineCommand(
    'topicMutate',
    'Topic Mutate',
    (r, [id]: [string]) => {
        r.call(EditorSession).update(AdapterInfo, CreateAdapterInfo(id, 'field'));
    },
    [],
);


const topicDef = {
    name: 'Topic',
    implements: [Physical, AutoBringToFront, HasCousins, PreferredTextColor, Votable, TagSupport],
    invocation,
    mutation,
    propertyLayout: topicPropertyLayout,
};

const topicPolicy = ComponentPolicy.create({
    allowAdd(child) {
        if (child.is(Physical)) return [false, 'Topic does not support children.'];
        return [true];
    }
});

@component(topicDef)
export class Topic extends RectangularPhysical implements AutoBringToFront, HasCousins, TagSupport, PreferredTextColor {

    public static computeBoundary(topic: Topic, assignees: Iterable<Physical>) {

        const points = [] as Point[];

        for (const a of assignees) {
            // Bit weird to check this here but because sometimes the caller is offset from the event loop its possible in random scenarios
            // that an assignee is actually dead so lets just check and move on with our lives.
            if (!a.alive) continue;
            const ab = a.bounds(RelativeTo.Model);
            points.push(...ab.points());
        }

        const boundary = points.length
            ? Polyline
                .fromHull(topic.transform(RelativeTo.Model).inverse().apply(points.concat(topic.bounds(RelativeTo.Model).points())))
                .inflate(20)
            : null;

        return boundary;
    }

    @property({ type: 'string', default: '#673ab7', style: true })
    public color!: string;

    @field({ type: TopicLabel, valueOnly: true })
    public label: string;

    public get boundary() {
        return true;
    }

    @memoUsing('width', 'height', 'voteCount')
    public get layout() {
        return TopicLayout(this);
    }

    protected get policy() {
        return topicPolicy;
    }

    // for layout memo
    protected get voteCount() {
        return VoteCountAttr.get(this);
    }

    public cousins(): Iterable<Physical> {
        return this[Host].resolve(TopicIndex).lookup(this.id);
    }

    public getPreferredSize() {
        const hasVotes = this.children.find(x => x.is(Vote));
        const label = this.field('label') as TextField;
        const { w, h } = TextRuler.measureSize(label.value, label).add([2, 0]);
        return new Point(Math.max(w + (hasVotes ? 25 : 0), 100), h);
    }

    public testInteraction({ type, mpt }: PotentialInteraction, _force?: boolean, _selected?: boolean): [boolean, string] {
        const { layout } = this;
        const lpt = this.toRelative(mpt);
        // Test invoke on any of the annotations
        if (type === 'invoke') {
            for (const [id, area] of Object.entries(layout.tags)) {
                if (area.contains(lpt)) return [true, id];
            }
        }
        // Any non invokes on the whole frame
        if (this.localBounds.contains(lpt)) return [type !== 'invoke', 'default'];
        return [false, 'none'];
    }

    @memoAll()
    public queryTransformHints(singular: boolean) {
        return TransformHints({
            scaleX: false,
            scaleY: false,
            rotate: false,
            translate: true,
            visible: !singular,
        });
    }

    public getPreferredTextColor(): string {
        return Colors.contrastTextColorFor(this.color);
    }

    public toEmptyTuple(to?: Physical | RelativeTo): ComponentTuple {
        const ct = super.toShallowTuple(to);
        ct[2].label = '';
        ct[2]['label|value'] = '';
        return ct;
    }
}
